<template>
  <div id="layout-wrapper" :class="{ 'nav-collapsed': isNavCollapsed }">
    <NavigationBar @nav-toggle="handleNavToggle" />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid lh-page">
          <header class="lh-header">
            <div>
              <nav class="lh-breadcrumb">
                <a href="javascript:void(0)" @click="gotTo('employee-dashboard')">Dashboard</a>
                <span>/</span>
                <span>My leave</span>
              </nav>
              <h1 class="lh-title">Leave history</h1>
              <p class="lh-subtitle">Track applications, approvals, and time off taken</p>
            </div>
            <button type="button" class="lh-btn lh-btn-primary" @click="gotTo('employee-leave-apply')">
              <i class="bx bx-plus-circle"></i>
              Apply for leave
            </button>
          </header>

          <div class="lh-metrics">
            <div class="lh-metric lh-metric--indigo">
              <div class="lh-metric-icon"><i class="bx bx-file"></i></div>
              <div>
                <span class="lh-metric-label">Total requests</span>
                <strong class="lh-metric-value">{{ statistics.total }}</strong>
              </div>
            </div>
            <div class="lh-metric lh-metric--amber">
              <div class="lh-metric-icon"><i class="bx bx-time-five"></i></div>
              <div>
                <span class="lh-metric-label">Pending</span>
                <strong class="lh-metric-value">{{ statistics.pending }}</strong>
              </div>
            </div>
            <div class="lh-metric lh-metric--green">
              <div class="lh-metric-icon"><i class="bx bx-check-circle"></i></div>
              <div>
                <span class="lh-metric-label">Approved</span>
                <strong class="lh-metric-value">{{ statistics.approved }}</strong>
              </div>
            </div>
            <div class="lh-metric lh-metric--sky">
              <div class="lh-metric-icon"><i class="bx bx-calendar-check"></i></div>
              <div>
                <span class="lh-metric-label">Days taken</span>
                <strong class="lh-metric-value">{{ statistics.totalDaysApproved }}</strong>
              </div>
            </div>
          </div>

          <div class="lh-toolbar">
            <div class="lh-filters">
              <label class="lh-filter">
                <span>Status</span>
                <select v-model="filters.status" class="lh-select" @change="loadLeaveHistory">
                  <option value="">All statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="UNUSED">Unused</option>
                </select>
              </label>
              <label class="lh-filter">
                <span>Year</span>
                <select v-model="filters.year" class="lh-select" @change="loadLeaveHistory">
                  <option :value="currentYear">{{ currentYear }}</option>
                  <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                  <option :value="currentYear - 2">{{ currentYear - 2 }}</option>
                </select>
              </label>
            </div>
            <button type="button" class="lh-btn lh-btn-ghost" @click="loadLeaveHistory">
              <i class="bx bx-refresh"></i>
              Refresh
            </button>
          </div>

          <div v-if="loading" class="lh-loading">
            <div class="lh-spinner"></div>
            <p>Loading your leave history…</p>
          </div>

          <div v-else class="lh-list-wrap">
            <div v-if="leaveRequests.length === 0" class="lh-empty">
              <div class="lh-empty-icon"><i class="bx bx-calendar-x"></i></div>
              <h3>No leave requests yet</h3>
              <p>When you apply for leave, your requests will show up here with status and dates.</p>
              <button type="button" class="lh-btn lh-btn-primary" @click="gotTo('employee-leave-apply')">
                <i class="bx bx-plus"></i>
                Apply for leave
              </button>
            </div>

            <template v-else>
              <article
                v-for="request in leaveRequests"
                :key="request.id"
                class="lh-request"
                :class="getStatusAccentClass(request.status)"
              >
                <div class="lh-request-main">
                  <div class="lh-request-icon" :class="getStatusIconClass(request.status)">
                    <i :class="getLeaveIconBx(request.status)"></i>
                  </div>
                  <div class="lh-request-body">
                    <div class="lh-request-top">
                      <h3 class="lh-request-title">{{ request.leaveTypeName }}</h3>
                      <span class="lh-pill" :class="getStatusPillClass(request.status)">
                        {{ formatStatusLabel(request.status) }}
                      </span>
                    </div>
                    <div class="lh-request-meta">
                      <span><i class="bx bx-calendar"></i>{{ formatDate(request.startDate) }} – {{ formatDate(request.endDate) }}</span>
                      <span class="lh-days-chip">{{ request.totalDays }} day{{ request.totalDays === 1 ? '' : 's' }}</span>
                      <span><i class="bx bx-time"></i>Applied {{ formatDate(request.createdAt) }}</span>
                    </div>
                    <p v-if="request.reason" class="lh-request-reason">{{ request.reason }}</p>
                    <div v-if="request.approvedBy || request.rejectedBy" class="lh-request-actor">
                      <span v-if="request.approvedBy" class="lh-actor lh-actor--ok">
                        <i class="bx bx-user-check"></i> Approved by {{ request.approvedBy }}
                      </span>
                      <span v-if="request.rejectedBy" class="lh-actor lh-actor--bad">
                        <i class="bx bx-user-x"></i> Rejected by {{ request.rejectedBy }}
                      </span>
                    </div>
                    <p v-if="request.rejectionReason" class="lh-rejection-note">
                      <i class="bx bx-error-circle"></i> {{ request.rejectionReason }}
                    </p>
                  </div>
                </div>
                <div class="lh-request-actions">
                  <button type="button" class="lh-btn lh-btn-soft" @click="viewDetails(request)">
                    <i class="bx bx-show"></i> Details
                  </button>
                  <button
                    v-if="request.documents"
                    type="button"
                    class="lh-btn lh-btn-soft"
                    @click="viewDocument(request.documents)"
                  >
                    <i class="bx bx-paperclip"></i> Document
                  </button>
                  <button
                    v-if="isPendingStatus(request.status)"
                    type="button"
                    class="lh-btn lh-btn-danger-soft"
                    @click="openCancelModal(request)"
                  >
                    <i class="bx bx-x-circle"></i> Cancel
                  </button>
                </div>
              </article>

              <div v-if="pagination.totalPages > 1" class="lh-pagination">
                <p class="lh-pagination-info">
                  Showing {{ ((pagination.currentPage - 1) * pagination.limit) + 1 }}–{{ Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords) }}
                  of {{ pagination.totalRecords }}
                </p>
                <div class="lh-pagination-nav">
                  <button
                    type="button"
                    class="lh-page-btn"
                    :disabled="pagination.currentPage === 1"
                    @click="changePage(pagination.currentPage - 1)"
                  >
                    <i class="bx bx-chevron-left"></i>
                  </button>
                  <button
                    v-for="page in visiblePages"
                    :key="'p-' + page"
                    type="button"
                    class="lh-page-btn"
                    :class="{ active: page === pagination.currentPage, ellipsis: page === '...' }"
                    :disabled="page === '...'"
                    @click="page !== '...' && changePage(page)"
                  >
                    {{ page }}
                  </button>
                  <button
                    type="button"
                    class="lh-page-btn"
                    :disabled="pagination.currentPage === pagination.totalPages"
                    @click="changePage(pagination.currentPage + 1)"
                  >
                    <i class="bx bx-chevron-right"></i>
                  </button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Cancel Modal -->
    <Teleport to="body">
      <div v-if="showCancelModal" class="leave-modal-overlay" @click.self="closeCancelModal">
        <div class="leave-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="cancel-leave-title">
          <div class="leave-modal-card">
            <div class="leave-modal-header">
              <div>
                <p class="leave-modal-eyebrow">Confirm action</p>
                <h5 id="cancel-leave-title" class="leave-modal-title">Cancel leave request</h5>
              </div>
              <button type="button" class="leave-modal-close" aria-label="Close" @click="closeCancelModal">
                <i class="bx bx-x"></i>
              </button>
            </div>
            <div class="leave-modal-body">
              <p class="lh-cancel-text">This will withdraw your pending application. You can submit a new request later if needed.</p>
              <div v-if="selectedRequest" class="lh-cancel-preview">
                <strong>{{ selectedRequest.leaveTypeName }}</strong>
                <span>{{ formatDate(selectedRequest.startDate) }} – {{ formatDate(selectedRequest.endDate) }} · {{ selectedRequest.totalDays }} days</span>
              </div>
              <label class="lh-cancel-label">
                Reason for cancellation <span>(optional)</span>
                <textarea v-model="cancelReason" class="lh-cancel-textarea" rows="3" placeholder="Add a short note…"></textarea>
              </label>
            </div>
            <div class="leave-modal-footer">
              <button type="button" class="leave-modal-btn leave-modal-btn-secondary" @click="closeCancelModal">Keep request</button>
              <button type="button" class="leave-modal-btn leave-modal-btn-danger" :disabled="cancelling" @click="confirmCancel">
                <span v-if="cancelling" class="lh-btn-spinner"></span>
                <i v-else class="bx bx-x-circle"></i>
                {{ cancelling ? 'Cancelling…' : 'Yes, cancel request' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Details Modal -->
    <Teleport to="body">
      <div v-if="showDetailsModal" class="leave-modal-overlay" @click.self="closeDetailsModal">
        <div class="leave-modal-dialog leave-modal-dialog-lg" role="dialog" aria-modal="true" aria-labelledby="leave-details-title">
          <div class="leave-modal-card">
            <div class="leave-modal-header">
              <div>
                <p class="leave-modal-eyebrow">Leave request</p>
                <h5 id="leave-details-title" class="leave-modal-title">Application details</h5>
              </div>
              <button type="button" class="leave-modal-close" aria-label="Close" @click="closeDetailsModal">
                <i class="bx bx-x"></i>
              </button>
            </div>
            <div class="leave-modal-body">
              <LeaveDetailsPanel v-if="selectedRequest" :leave="selectedRequest" />
            </div>
            <div class="leave-modal-footer">
              <button type="button" class="leave-modal-btn leave-modal-btn-secondary" @click="closeDetailsModal">
                Close
              </button>
              <button
                v-if="selectedRequest && isPendingStatus(selectedRequest.status)"
                type="button"
                class="leave-modal-btn leave-modal-btn-danger"
                @click="openCancelFromDetails"
              >
                <i class="bx bx-x-circle"></i>
                Cancel request
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import LeaveDetailsPanel from "@/components/leaveAndAttendance/LeaveDetailsPanel.vue";
import { authUrl, execute, uploads } from "@/api";

export default {
  name: "EmployeeLeaveHistory",
  components: { NavigationBar, LeaveDetailsPanel },

  data() {
    return {
      isNavCollapsed: false,
      loading: false,
      cancelling: false,
      leaveRequests: [],
      statistics: {
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
        cancelled: 0,
        totalDaysApproved: 0
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 10
      },
      filters: {
        status: '',
        year: new Date().getFullYear()
      },
      currentYear: new Date().getFullYear(),
      selectedRequest: null,
      cancelReason: '',
      showCancelModal: false,
      showDetailsModal: false
    };
  },

  computed: {
    visiblePages() {
      const pages = [];
      const total = this.pagination.totalPages;
      const current = this.pagination.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    }
  },

  mounted() {
    this.cleanupBootstrapModalArtifacts();
    this.loadLeaveHistory();
  },

  beforeUnmount() {
    this.cleanupBootstrapModalArtifacts();
    document.body.style.overflow = '';
  },

  methods: {
    cleanupBootstrapModalArtifacts() {
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    },

    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    async loadLeaveHistory() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getEmployeeLeaveHistory");
        data.append("status", this.filters.status);
        data.append("year", this.filters.year);
        data.append("page", this.pagination.currentPage);
        data.append("limit", this.pagination.limit);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.leaveRequests = res.data.data.leaveRequests;
          this.statistics = res.data.data.statistics;
          this.pagination = res.data.data.pagination;
        } else {
          throw new Error(res.data?.message || 'Failed to load leave history');
        }
      } catch (error) {
        this.$toast?.error("Failed to load leave history: " + error.message) ||
        console.error("Failed to load leave history:", error);
      } finally {
        this.loading = false;
      }
    },

    openCancelModal(request) {
      this.cleanupBootstrapModalArtifacts();
      this.selectedRequest = request;
      this.cancelReason = '';
      this.showDetailsModal = false;
      this.showCancelModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeCancelModal() {
      this.showCancelModal = false;
      this.selectedRequest = null;
      this.cancelReason = '';
      if (!this.showDetailsModal) {
        document.body.style.overflow = '';
      }
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      if (!this.showCancelModal) {
        document.body.style.overflow = '';
      }
    },

    async confirmCancel() {
      if (!this.selectedRequest) return;

      this.cancelling = true;

      try {
        const data = new FormData();
        data.append("function", "cancelLeaveRequest");
        data.append("leaveRequestId", this.selectedRequest.id);
        data.append("cancelReason", this.cancelReason || 'Cancelled by employee');

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.$toast?.success("Leave request cancelled successfully") ||
          alert("Leave request cancelled successfully");

          this.closeCancelModal();

          this.loadLeaveHistory();
        } else {
          throw new Error(res.data?.message || 'Failed to cancel leave request');
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) ||
        alert("Error: " + error.message);
      } finally {
        this.cancelling = false;
      }
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.pagination.currentPage) {
        return;
      }
      this.pagination.currentPage = page;
      this.loadLeaveHistory();
    },

    viewDocument(documentPath) {
      if (documentPath) {
        window.open(uploads + documentPath, '_blank');
      }
    },

    viewDetails(request) {
      this.cleanupBootstrapModalArtifacts();
      this.selectedRequest = request;
      this.showCancelModal = false;
      this.showDetailsModal = true;
      document.body.style.overflow = 'hidden';
    },

    openCancelFromDetails() {
      this.showDetailsModal = false;
      this.showCancelModal = true;
      document.body.style.overflow = 'hidden';
    },

    formatDateTime(datetime) {
      if (!datetime) return 'N/A';
      const d = new Date(datetime);
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return d.toLocaleDateString('en-US', options);
    },

    getDetailStatusClass(status) {
      const statusLower = status?.toLowerCase();
      if (statusLower === 'approved') return 'alert-success';
      if (statusLower === 'rejected') return 'alert-danger';
      if (statusLower === 'pending') return 'alert-warning';
      if (statusLower === 'cancelled') return 'alert-secondary';
      if (statusLower === 'unused') return 'alert-secondary';
      return 'alert-info';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    },

    formatStatusLabel(status) {
      const s = String(status || "Unknown").toLowerCase();
      if (s === "pending") return "Pending";
      if (s === "approved") return "Approved";
      if (s === "rejected") return "Rejected";
      if (s === "cancelled") return "Cancelled";
      if (s === "unused") return "Unused";
      return status || "Unknown";
    },

    getStatusPillClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "approved") return "lh-pill--approved";
      if (s === "rejected") return "lh-pill--rejected";
      if (s === "pending") return "lh-pill--pending";
      if (s === "cancelled") return "lh-pill--cancelled";
      if (s === "unused") return "lh-pill--cancelled";
      return "lh-pill--default";
    },

    getStatusAccentClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "approved") return "lh-request--approved";
      if (s === "rejected") return "lh-request--rejected";
      if (s === "pending") return "lh-request--pending";
      if (s === "cancelled") return "lh-request--cancelled";
      if (s === "unused") return "lh-request--cancelled";
      return "";
    },

    getStatusIconClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "approved") return "lh-request-icon--approved";
      if (s === "rejected") return "lh-request-icon--rejected";
      if (s === "pending") return "lh-request-icon--pending";
      if (s === "cancelled") return "lh-request-icon--cancelled";
      if (s === "unused") return "lh-request-icon--cancelled";
      return "lh-request-icon--default";
    },

    getLeaveIconBx(status) {
      const s = String(status || "").toLowerCase();
      if (s === "approved") return "bx bx-check-circle";
      if (s === "rejected") return "bx bx-x-circle";
      if (s === "pending") return "bx bx-time-five";
      if (s === "cancelled") return "bx bx-block";
      if (s === "unused") return "bx bx-undo";
      return "bx bx-file";
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    },

    isPendingStatus(status) {
      return String(status || "").toLowerCase() === "pending";
    }
  }
};
</script>

<style scoped>
#layout-wrapper {
  min-height: 100vh;
  box-sizing: border-box;
}

.content-minimize {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

#layout-wrapper.nav-collapsed .content-minimize {
  margin-left: 70px;
}

@media (max-width: 1024px) {
  .content-minimize {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .content-minimize {
    margin-left: 0;
    padding: 15px;
  }
  #layout-wrapper.nav-collapsed .content-minimize {
    margin-left: 0;
  }
}

/* Leave history page */
.lh-page {
  padding-bottom: 2rem;
  max-width: 1100px;
}

.lh-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.lh-breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: #94a3b8;
  margin-bottom: 0.35rem;
}

.lh-breadcrumb a {
  color: #6366f1;
  text-decoration: none;
  font-weight: 500;
}

.lh-breadcrumb a:hover {
  text-decoration: underline;
}

.lh-title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.lh-subtitle {
  margin: 0.35rem 0 0;
  font-size: 0.95rem;
  color: #64748b;
}

.lh-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.lh-metric {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.1rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: transform 0.2s, box-shadow 0.2s;
}

.lh-metric:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.lh-metric-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.lh-metric--indigo .lh-metric-icon {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #4f46e5;
}

.lh-metric--amber .lh-metric-icon {
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  color: #d97706;
}

.lh-metric--green .lh-metric-icon {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  color: #059669;
}

.lh-metric--sky .lh-metric-icon {
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  color: #0284c7;
}

.lh-metric-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 0.15rem;
}

.lh-metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
}

.lh-toolbar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.15rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin-bottom: 1.25rem;
}

.lh-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.lh-filter {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
}

.lh-select {
  min-width: 160px;
  padding: 0.55rem 2rem 0.55rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #0f172a;
  background: #f8fafc url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E") no-repeat right 0.65rem center;
  appearance: none;
  cursor: pointer;
}

.lh-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.lh-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 1rem;
  border-radius: 10px;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s, background 0.15s;
}

.lh-btn:active {
  transform: scale(0.98);
}

.lh-btn-primary {
  background: linear-gradient(135deg, #4f46e5, #6366f1);
  color: #fff;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35);
}

.lh-btn-primary:hover {
  box-shadow: 0 6px 18px rgba(99, 102, 241, 0.45);
}

.lh-btn-ghost {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.lh-btn-ghost:hover {
  background: #f1f5f9;
}

.lh-btn-soft {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid transparent;
  padding: 0.45rem 0.85rem;
  font-size: 0.85rem;
}

.lh-btn-soft:hover {
  background: #e2e8f0;
}

.lh-btn-danger-soft {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 0.45rem 0.85rem;
  font-size: 0.85rem;
}

.lh-btn-danger-soft:hover {
  background: #fee2e2;
}

.lh-loading {
  text-align: center;
  padding: 4rem 1rem;
  color: #64748b;
}

.lh-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: lhSpin 0.7s linear infinite;
}

@keyframes lhSpin {
  to { transform: rotate(360deg); }
}

.lh-empty {
  text-align: center;
  padding: 3.5rem 1.5rem;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 20px;
}

.lh-empty-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1.25rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #94a3b8;
}

.lh-empty h3 {
  margin: 0 0 0.5rem;
  font-size: 1.15rem;
  color: #0f172a;
}

.lh-empty p {
  margin: 0 0 1.25rem;
  color: #64748b;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
}

.lh-list-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.lh-request {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1.15rem 1.25rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  border-left-width: 4px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s, transform 0.2s;
}

.lh-request:hover {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.lh-request--pending { border-left-color: #f59e0b; }
.lh-request--approved { border-left-color: #10b981; }
.lh-request--rejected { border-left-color: #ef4444; }
.lh-request--cancelled { border-left-color: #94a3b8; }

.lh-request-main {
  display: flex;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.lh-request-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
}

.lh-request-icon--pending { background: #fffbeb; color: #d97706; }
.lh-request-icon--approved { background: #ecfdf5; color: #059669; }
.lh-request-icon--rejected { background: #fef2f2; color: #dc2626; }
.lh-request-icon--cancelled { background: #f1f5f9; color: #64748b; }
.lh-request-icon--default { background: #f1f5f9; color: #64748b; }

.lh-request-body {
  flex: 1;
  min-width: 0;
}

.lh-request-top {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
  margin-bottom: 0.4rem;
}

.lh-request-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.lh-pill {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.lh-pill--pending { background: #fef3c7; color: #b45309; }
.lh-pill--approved { background: #d1fae5; color: #047857; }
.lh-pill--rejected { background: #fee2e2; color: #b91c1c; }
.lh-pill--cancelled { background: #f1f5f9; color: #475569; }
.lh-pill--default { background: #f1f5f9; color: #64748b; }

.lh-request-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem 1rem;
  font-size: 0.85rem;
  color: #64748b;
  margin-bottom: 0.5rem;
}

.lh-request-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.lh-days-chip {
  background: #f1f5f9;
  padding: 0.1rem 0.5rem;
  border-radius: 6px;
  font-weight: 600;
  color: #334155;
}

.lh-request-reason {
  margin: 0 0 0.5rem;
  font-size: 0.9rem;
  color: #475569;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.lh-request-actor {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.8rem;
  margin-bottom: 0.35rem;
}

.lh-actor {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.lh-actor--ok { color: #059669; }
.lh-actor--bad { color: #dc2626; }

.lh-rejection-note {
  margin: 0.5rem 0 0;
  padding: 0.55rem 0.75rem;
  background: #fef2f2;
  border-radius: 10px;
  font-size: 0.85rem;
  color: #b91c1c;
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
}

.lh-request-actions {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-self: center;
  flex-shrink: 0;
}

.lh-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
  padding: 1rem 1.15rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.lh-pagination-info {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
}

.lh-pagination-nav {
  display: flex;
  gap: 0.35rem;
}

.lh-page-btn {
  min-width: 36px;
  height: 36px;
  padding: 0 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #475569;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.lh-page-btn:hover:not(:disabled):not(.ellipsis) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.lh-page-btn.active {
  background: #6366f1;
  border-color: #6366f1;
  color: #fff;
}

.lh-page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.lh-page-btn.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}

.lh-cancel-text {
  margin: 0 0 1rem;
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
}

.lh-cancel-preview {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.85rem 1rem;
  background: linear-gradient(135deg, #f0f9ff, #eef2ff);
  border-radius: 12px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #334155;
}

.lh-cancel-preview strong {
  color: #0f172a;
  font-size: 1rem;
}

.lh-cancel-label {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
}

.lh-cancel-label span {
  font-weight: 400;
  color: #94a3b8;
}

.lh-cancel-textarea {
  padding: 0.65rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.9rem;
  resize: vertical;
  min-height: 80px;
}

.lh-cancel-textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}

.lh-btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: lhSpin 0.6s linear infinite;
}

@media (max-width: 992px) {
  .lh-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .lh-header {
    flex-direction: column;
  }

  .lh-header .lh-btn-primary {
    width: 100%;
    justify-content: center;
  }

  .lh-metrics {
    grid-template-columns: 1fr 1fr;
  }

  .lh-request {
    flex-direction: column;
  }

  .lh-request-actions {
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    align-self: stretch;
  }

  .lh-pagination {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .lh-pagination-nav {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .lh-metrics {
    grid-template-columns: 1fr;
  }
}

.leave-modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 1rem;
}

.leave-modal-dialog {
  width: 100%;
  max-width: 500px;
  margin: auto;
  position: relative;
  z-index: 2001;
  pointer-events: auto;
}

.leave-modal-dialog-lg {
  max-width: 720px;
}

.leave-modal-card {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
  overflow: hidden;
  animation: leaveModalIn 0.25s ease-out;
}

@keyframes leaveModalIn {
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.leave-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px 0;
}

.leave-modal-eyebrow {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.leave-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
}

.leave-modal-close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.leave-modal-close:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.leave-modal-body {
  padding: 20px 24px;
  max-height: min(70vh, 640px);
  overflow-y: auto;
}

.leave-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.leave-modal-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s;
}

.leave-modal-btn:active {
  transform: scale(0.98);
}

.leave-modal-btn-secondary {
  background: #fff;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.leave-modal-btn-secondary:hover {
  background: #f8fafc;
}

.leave-modal-btn-danger {
  background: linear-gradient(135deg, #dc2626, #ef4444);
  color: #fff;
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.35);
}

.leave-modal-btn-danger:hover {
  box-shadow: 0 6px 18px rgba(239, 68, 68, 0.4);
}
</style>